import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAllInventoryPaginated,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, MapPin, AlertCircle, Loader2 } from "lucide-react";
import { PRODUCT_CATEGORIES } from "@shared/categories";
import mapBg from "@assets/generated_images/minimalist_abstract_city_map_pattern.png";

// Función para obtener símbolo de moneda basado en código
const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    CUP: "$",
    L: "L",
    MXN: "$",
  };
  return symbols[currencyCode] || "$";
};

export default function Home() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [minPrice, setMinPrice] = useState<number | "">("");
  const [maxPrice, setMaxPrice] = useState<number | "">("");
  const [sortBy, setSortBy] = useState<"price-asc" | "price-desc" | "name">(
    "price-asc",
  );

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Obtener inventario con paginación
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: [
      "inventory",
      currentPage,
      itemsPerPage,
      debouncedSearch,
      selectedCategory,
      minPrice,
      maxPrice,
      sortBy,
    ],
    queryFn: () =>
      getAllInventoryPaginated({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        category: selectedCategory,
        minPrice: minPrice !== "" ? minPrice : undefined,
        maxPrice: maxPrice !== "" ? maxPrice : undefined,
        sortBy: sortBy,
      }),
    staleTime: 30000,
  });

  // Debounce para la búsqueda (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // LOGS PARA DEPURACIÓN - AÑADIR DESPUÉS DE LA QUERY
  useEffect(() => {
    if (inventoryData?.data && inventoryData.data.length > 0) {
      console.log("📦 [FRONTEND] Primer item recibido:", inventoryData.data[0]);
      console.log("🔍 [FRONTEND] Estructura completa:", {
        tieneProduct: !!inventoryData.data[0].product,
        productCategory: inventoryData.data[0].product?.category,
        productName: inventoryData.data[0].product?.name,
        shopName: inventoryData.data[0].shop?.name,
        keys: Object.keys(inventoryData.data[0]),
      });
    } else {
      console.log("📦 [FRONTEND] No hay datos o data vacía:", inventoryData);
    }
  }, [inventoryData]);

  // Calcular total de páginas cuando cambia el total de items
  useEffect(() => {
    if (inventoryData?.total) {
      setTotalItems(inventoryData.total);
      setTotalPages(Math.ceil(inventoryData.total / itemsPerPage));
    }
  }, [inventoryData?.total, itemsPerPage]);

  // Extraer datos de la respuesta paginada
  const inventory = inventoryData?.data || [];
  const inventoryTotal = inventoryData?.total || 0;

  // Solo filtramos por stock > 0 (por si acaso)
  // Los demás filtros (search, category, price, sortBy) ya los aplica el backend
  const filteredInventory = inventory.filter((item) => item.stock > 0);

  // Usar las categorías predefinidas
  const categories = ["all", ...PRODUCT_CATEGORIES];

  const isLoading = inventoryLoading;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Hero Search Section */}
      <div
        className="relative bg-muted flex flex-col items-center justify-center px-4 py-8 md:py-16 text-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0.8), rgba(255,255,255,0.95)), url(${mapBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-4xl w-full">
          <h1 className="text-3xl md:text-6xl font-heading font-bold text-foreground mb-4 tracking-tight">
            ENCUENTRA LO QUE BUSCAS
          </h1>
          <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto">
            Busca productos en puntos de venta cercanos. Ahorra tiempo y dinero
            sin caminar de más.
          </p>

          {/* Contenedor de filtros simplificado para móvil */}
          <div className="w-full bg-card p-4 rounded-lg shadow-lg">
            {/* Búsqueda principal */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="¿Qué estás buscando?"
                  className="pl-9 text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="input-search"
                />
              </div>
            </div>

            {/* Filtros en grid para móvil */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Categoría */}
              <div>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all" className="text-sm">
                      Todas las categorías
                    </SelectItem>
                    {PRODUCT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-sm">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ordenar por */}
              <div>
                <Select
                  value={sortBy}
                  onValueChange={(value: any) => setSortBy(value)}
                >
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price-asc" className="text-sm">
                      Precio: Menor a Mayor
                    </SelectItem>
                    <SelectItem value="price-desc" className="text-sm">
                      Precio: Mayor a Menor
                    </SelectItem>
                    <SelectItem value="name" className="text-sm">
                      Nombre: A-Z
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Precio mínimo */}
              <div className="relative">
                <Label htmlFor="min-price-mobile" className="sr-only">
                  Precio desde
                </Label>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  $
                </span>
                <Input
                  id="min-price-mobile"
                  type="number"
                  placeholder="Precio desde"
                  className="pl-8 text-sm"
                  value={minPrice}
                  onChange={(e) =>
                    setMinPrice(e.target.value ? Number(e.target.value) : "")
                  }
                  min="0"
                />
              </div>

              {/* Precio máximo */}
              <div className="relative">
                <Label htmlFor="max-price-mobile" className="sr-only">
                  Precio hasta
                </Label>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  $
                </span>
                <Input
                  id="max-price-mobile"
                  type="number"
                  placeholder="Precio hasta"
                  className="pl-8 text-sm"
                  value={maxPrice}
                  onChange={(e) =>
                    setMaxPrice(e.target.value ? Number(e.target.value) : "")
                  }
                  min="0"
                />
              </div>
            </div>

            {/* Botón para limpiar filtros (solo se muestra cuando hay filtros activos) */}
            {(minPrice !== "" ||
              maxPrice !== "" ||
              selectedCategory !== "all" ||
              searchTerm !== "") && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMinPrice("");
                    setMaxPrice("");
                    setSearchTerm("");
                    setSelectedCategory("all");
                    setSortBy("price-asc");
                  }}
                  className="w-full text-sm"
                >
                  Limpiar Todos los Filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Grid */}
      <div className="container mx-auto px-4 py-12 flex-1">
        {/* Título y resultados en columna */}
        <div className="mb-6">
          <h2 className="text-xl sm:text-2xl font-heading font-bold mb-2">
            Resultados Disponibles
          </h2>

          {/* Información de resultados DEBAJO del título */}
          <div
            className="text-sm text-muted-foreground"
            data-testid="text-results-count"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              {/* Contador principal */}
              <span>
                {isLoading
                  ? "Cargando..."
                  : `${inventoryTotal} productos encontrados`}
                {inventory.length < inventoryTotal &&
                  ` (${inventory.length} en esta página)`}
              </span>

              {/* Información de ordenamiento - SOLO DESKTOP */}
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">
                {sortBy === "price-asc" &&
                  "Ordenado por precio (menor a mayor)"}
                {sortBy === "price-desc" &&
                  "Ordenado por precio (mayor a menor)"}
                {sortBy === "name" && "Ordenado por nombre"}
              </span>
            </div>

            {/* Versión móvil del ordenamiento (debajo) */}
            <div className="sm:hidden mt-1">
              {sortBy === "price-asc" && "Ordenado por precio (menor a mayor)"}
              {sortBy === "price-desc" && "Ordenado por precio (mayor a menor)"}
              {sortBy === "name" && "Ordenado por nombre"}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="text-center py-20 bg-muted/20 rounded-xl border-2 border-dashed">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-bold mb-2">No encontramos productos</h3>
            <p className="text-muted-foreground">
              Intenta con otro término de búsqueda o categoría.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredInventory.map((item) => (
              <Card
                key={item.id}
                className="overflow-hidden hover:shadow-md transition-shadow group border-muted hover:border-primary/50"
                data-testid={`card-product-${item.id}`}
              >
                <CardContent className="p-6 pt-8">
                  {" "}
                  {/* Más padding superior */}
                  {/* 1. CATEGORÍA (arriba) */}
                  <Badge variant="secondary" className="mb-2 text-xs font-bold">
                    {item.product.category}
                  </Badge>
                  {/* 2. NOMBRE DEL PRODUCTO (debajo categoría) */}
                  <CardTitle
                    className="text-lg font-bold leading-tight mb-3"
                    data-testid={`text-product-name-${item.id}`}
                  >
                    {item.product.name}
                  </CardTitle>
                  {/* 3. PRECIO Y STOCK (misma línea, debajo nombre) */}
                  <div className="flex justify-between items-center mb-4">
                    {/* Precio */}
                    <div className="text-left">
                      <div className="flex items-baseline gap-1">
                        <span
                          className="text-xl font-heading font-bold text-primary"
                          data-testid={`text-price-${item.id}`}
                        >
                          {getCurrencySymbol(item.shop?.ownerCurrency || "CUP")}
                          {item.price}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {item.shop?.ownerCurrency || "CUP"}
                        </span>
                      </div>
                    </div>

                    {/* Stock */}
                    <div className="text-right">
                      <p
                        className={`text-sm flex items-center justify-end gap-1 ${
                          item.stock <= item.lowStockThreshold
                            ? "text-destructive font-bold"
                            : "text-muted-foreground"
                        }`}
                        data-testid={`text-stock-${item.id}`}
                      >
                        {item.stock <= item.lowStockThreshold && (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        Stock: {item.stock} unidades
                      </p>
                    </div>
                  </div>
                  {/* 4. INFORMACIÓN DE LA TIENDA (separada con borde) */}
                  <div className="mt-4 pt-4 border-t flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p
                        className="font-bold text-sm"
                        data-testid={`text-shop-name-${item.id}`}
                      >
                        {item.shop.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.shop.address}
                      </p>
                      {item.stock <= item.lowStockThreshold && (
                        <p className="text-xs text-destructive font-bold mt-1">
                          ¡Quedan pocos! ({item.stock})
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Paginación RESPONSIVE */}
        {totalPages > 1 && (
          <div className="mt-8 pt-6 border-t">
            {/* Primera fila: Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="text-sm text-gray-600">
                Mostrando{" "}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                -
                <span className="font-medium">
                  {" "}
                  {Math.min(currentPage * itemsPerPage, inventoryTotal)}
                </span>{" "}
                de
                <span className="font-medium"> {inventoryTotal}</span> productos
              </div>

              {/* Selector de items por página */}
              <div className="sm:self-center">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Resetear a página 1
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm w-full sm:w-auto"
                >
                  <option value="20">20 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </select>
              </div>
            </div>

            {/* Segunda fila: Navegación */}
            <div className="flex flex-col xs:flex-row items-center justify-between gap-3">
              {/* Botón Anterior */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
                className="w-full xs:w-auto order-2 xs:order-1"
              >
                Anterior
              </Button>

              {/* Números de página */}
              <div className="flex items-center gap-1 order-1 xs:order-2 overflow-x-auto pb-2 xs:pb-0 max-w-full">
                {/* Desktop */}
                <div className="hidden xs:flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="min-w-[40px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                {/* Mobile simplificado */}
                <div className="xs:hidden flex items-center gap-1">
                  <Button
                    variant={currentPage === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={isLoading}
                  >
                    1
                  </Button>

                  {currentPage > 3 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}

                  {currentPage > 2 && currentPage < totalPages - 1 && (
                    <Button
                      variant="default"
                      size="sm"
                      className="min-w-[40px]"
                    >
                      {currentPage}
                    </Button>
                  )}

                  {currentPage < totalPages - 2 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}

                  {totalPages > 1 && (
                    <Button
                      variant={
                        currentPage === totalPages ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={isLoading}
                    >
                      {totalPages}
                    </Button>
                  )}
                </div>

                {/* Indicador móvil */}
                <div className="xs:hidden text-sm text-gray-600 ml-2 whitespace-nowrap">
                  Pág. {currentPage} de {totalPages}
                </div>
              </div>

              {/* Botón Siguiente */}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages || isLoading}
                className="w-full xs:w-auto order-3"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
